import axios from "axios";
import { getAuthHeader } from "./auth"; 
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

/**
 * Fetches user details by userId
 * @param {string} userId - The user's ID
 * @returns {Promise<object>} - User data
 */

export const fetchUserData = async (setFormData, setError, setLoading) => {
  try {
    setLoading(true);
    const headers = await getAuthHeader();

    // Fetch user data from authenticated endpoint
    const response = await axios.get(`${API_BASE_URL}/api/users/me/`, { headers });
    const userData = response.data;

    // Address is optional (users can skip setup). Missing/empty address must not
    // fail the whole dashboard profile load.
    let addressData = {};
    try {
      const addressResponse = await axios.get(`${API_BASE_URL}/api/users/me/address/`, {
        headers,
        validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
      });
      addressData = addressResponse?.data || {};
    } catch (addressError) {
      console.warn(
        "[fetchUserData] Address fetch failed; continuing without address:",
        addressError?.response?.status,
        addressError?.message
      );
      addressData = {};
    }

    let updatedData = {
      ...userData,
      street_1: addressData?.street_1 || "",
      city: addressData?.city || "",
      state: addressData?.state || "",
      zipcode: addressData?.zipcode || "",
      district: userData.district_number || "",
      user_district_representative_fullname: userData.representatives_name || "",
      user_district_representative_bioguideid: userData.representative_bioguide_id || "",
      is_verified: userData.is_verified || false,
    };

    setFormData(updatedData);
    if (setError) setError(null);
  } catch (error) {
    console.error("Error fetching data:", error.response?.data || error.message);
    if (setError) setError("Failed to load profile data.");
  } finally {
    setLoading(false);
  }
};

/**
 * Fetches user address details
 * @param {string} userId - The user's ID
 * @returns {Promise<object>} - Address data
 */
export const fetchUserAddress = async (userId) => {
  try {
    if (!userId) throw new Error("User ID is missing!");

    const response = await axios.get(`${API_BASE_URL}/api/users/${userId}/user_address_view/`);
    return response.data.length > 0 ? response.data[0] : {};
  } catch (error) {
    console.error("Error fetching user address:", error.message);
    return {};
  }
};

export const fetchUserInformation = async (userId) => {
  try {
    if (!userId) throw new Error("Missing user ID!");
 
    // Fetch user data (Primary source for representative info)
    const response = await axios.get(`${API_BASE_URL}/api/user_details/${userId}/`);
    const userData = response.data;
    
    return {
      bioguideId: userData.representative_bioguide_id || null
    };
 
  } catch (error) {
    console.error("Error fetching data:", error.response?.data || error.message);
 
  }
};
 
export const startVerificationSession = async () => {
  const headers = await getAuthHeader();

  const res = await axios.post(
    `${API_BASE_URL}/api/start-verification/`,
    {},
    {
      headers: { ...headers, Accept: "application/json" },
      timeout: 15000,
      validateStatus: (s) => s >= 200 && s < 500, // let 4xx bubble with body
    }
  );

  if (res.status !== 200 || !res.data?.verification_url) {
    const msg =
      res.data?.error ||
      res.data?.detail ||
      `Unexpected response (status ${res.status})`;
    throw new Error(msg);
  }

  // optional: log to ensure you’re using live sessions in prod
  if (typeof res.data.livemode === "boolean") {
    console.log(`[Identity] livemode=${res.data.livemode ? "live" : "test"}`);
  }

  return { url: res.data.verification_url };
};